import { useMemo } from 'react'

import { Code } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { convertBusinessMasterListItems } from '~/hooks/businessMaster/useListBusinessMasters'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import {
  convertBgMasterListItems,
  convertListBgMastersResponse,
  useListBgMasters,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { DemandSuppressionPlanMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/components/DemandSuppressionPlanMasterFormFields'
import { useCreateDemandSuppressionMaster } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/hooks/useCreateDemandSuppressionMaster'
import { useGetDemandSuppressionPlanMasterFormOptions } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/hooks/useGetDemandSuppressionPlanMasterFormOptions'
import {
  type DemandSuppressionMasterFormValues,
  demandSuppressionMasterFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/models/schema'
import { resolveDemandSuppressionMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/models/serverError'
import { convertSubmitterMasterListItems } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { findBgsOutOfPeriod } from '~/pages/AdminOcctoMaster/tabs/_common/models/planMasterBgValidation'

type Props = {
  isOpen: boolean
  onClose: () => void
}

/**
 * 需要抑制計画マスタ 新規登録モーダル。
 *
 * submitterMasterId は計画提出者マスタのドロップダウンで選択する。
 * submitterCode は選択した提出者から自動取得する。
 * contracts / partnerBgs は後から Edit モーダルで追加する。
 */
export const CreateDemandSuppressionPlanMasterModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreateDemandSuppressionMaster()
  // 集約 RPC: 区分フィルタ済みの submitters / suppressionBgs / partnerBgs / businessMasters を取得
  const { data: formOptions, isPending: isLoadingFormOptions } =
    useGetDemandSuppressionPlanMasterFormOptions()
  // 削除済み判定専用: 絞り込み前の全 BG マスタ (4526-plan-master-valid-period-filter
  // 設計書 §4、AC-06)。
  const { data: allBgMastersData } = useListBgMasters()

  // 集約 RPC の submitters (DR 区分フィルタ済み) → submitter ドロップダウン用
  const submitters = useMemo(
    () =>
      formOptions !== undefined
        ? convertSubmitterMasterListItems(formOptions.submitters)
        : [],
    [formOptions],
  )
  // list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、
  // PR #5920 レビュー指摘)。
  const allBgMasters = useMemo(
    () =>
      allBgMastersData !== undefined
        ? convertListBgMastersResponse(allBgMastersData)
        : undefined,
    [allBgMastersData],
  )
  // 集約 RPC の suppressionBgs (DR 区分フィルタ済み) → SuppressionContractsSection 用
  const suppressionBgs = useMemo(
    () =>
      formOptions !== undefined
        ? convertBgMasterListItems(formOptions.suppressionBgs)
        : [],
    [formOptions],
  )
  // 集約 RPC の partnerBgs (全区分) → SuppressionContractsSection + SuppressionPartnerBgsSection 用
  const partnerBgs = useMemo(
    () =>
      formOptions !== undefined
        ? convertBgMasterListItems(formOptions.partnerBgs)
        : [],
    [formOptions],
  )
  // 集約 RPC の businesses → SuppressionContractsSection 用 (proto field: businesses)
  const businessMasters = useMemo(
    () =>
      formOptions !== undefined
        ? convertBusinessMasterListItems(formOptions.businesses)
        : [],
    [formOptions],
  )

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave: hookHandleSave,
    setValue,
    watch,
    setError,
    getValues,
    isDirty,
  } = useCrudModalForm<DemandSuppressionMasterFormValues>({
    schema: demandSuppressionMasterFormSchema,
    defaultValues: {
      submitterMasterId: '',
      submitterCode: '',
      note: '',
      contracts: [],
      partnerBgs: [],
      validFrom: todayCivilDate(),
      validTo: undefined,
    },
    onSubmit: async (values) => {
      await mutateAsync({
        submitterMasterId: values.submitterMasterId,
        submitterCode: values.submitterCode,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        // contracts / partnerBgs は fieldArray バインドで入力された値を送出する (RG6)
        contracts: values.contracts.map((c) => ({
          suppressionBgMasterId: c.suppressionBgMasterId,
          partnerBgMasterId: c.partnerBgMasterId,
          businessMasterId: c.businessMasterId,
          contractId: c.contractId,
          validFrom: c.validFrom,
          validTo: c.validTo,
        })),
        partnerBgs: values.partnerBgs.map((p) => ({
          side: p.side,
          bgMasterId: p.bgMasterId,
          validFrom: p.validFrom,
          validTo: p.validTo,
        })),
        validFrom: values.validFrom,
        validTo: values.validTo,
      })
    },
    onClose,
    customErrorKeys: {
      [Code.Aborted]:
        'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.aborted',
      [Code.NotFound]:
        'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.notFound',
      [Code.InvalidArgument]:
        'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.invalidArgument',
    },
    unknownErrorKey:
      'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError.unknown',
    // AC-OMD-001a: registry の SUBMITTER_ALREADY_HAS_SUPPRESSION_MASTER
    // entry は spec の動的文言 (submitterCode) を含む。submit 済み form 値
    // (values、N3 fresh 値契約) から必要な placeholder のみを明示合成して
    // 渡す (GSP の CreateGenerationSalesPlanMasterModal と同形)。
    resolveDetailedError: (err, t, values) =>
      resolveDemandSuppressionMasterDetailedError(err, t, {
        submitterCode: values.submitterCode,
      }),
  })

  // 選択した計画提出者の areaCode を解決して BG 候補フィルタに使う
  const selectedSubmitterCode = watch('submitterCode')
  const submitterAreaCode = useMemo(() => {
    const code = selectedSubmitterCode
    if (code === '' || code === undefined) return undefined
    return submitters.find((s) => s.submitterCode === code)?.areaCode
  }, [selectedSubmitterCode, submitters])

  // AC-03: 計画提出者 select の候補をレコードの適用開始日 (validFrom) 時点で
  // 絞り込む (需要抑制 Create modal のみ、Edit は submitter readonly のため対象外。
  // 4526-plan-master-valid-period-filter 設計書 §4 Q3-2)。
  const watchedRecordValidFrom = watch('validFrom')
  const submitterCandidates = useMemo(
    () =>
      submitters.filter(
        (s) =>
          watchedRecordValidFrom === undefined ||
          isActiveOn(s.validFrom, s.validTo, watchedRecordValidFrom),
      ),
    [submitters, watchedRecordValidFrom],
  )

  // 期間外の submitter / 期間外の 3 種 BG (suppressionBg / partnerBg / partnerBgs) を
  // hook の handleSave 前に個別 field-level エラーで block する。
  const validateOutOfPeriodBeforeSubmit = (): boolean => {
    const values = getValues()
    // 選択済み計画提出者が validFrom narrow 後の候補外なら submit を block
    // する (fallback option で UI 上は残るが、そのまま save すると失効 submitter
    // が RPC 送信される silent invalid submission を防ぐ)。
    if (
      values.submitterMasterId !== '' &&
      !submitterCandidates.some((s) => s.id === values.submitterMasterId)
    ) {
      setError('submitterMasterId', {
        message: t(
          'page:adminOcctoMaster.demandSuppressionPlanMaster.form.submitterMasterOutOfPeriod',
        ),
      })
      return false
    }

    // contracts / partnerBgs の選択済み BG が validFrom narrow 後の候補外
    // (= 有効期間外) なら submit を block する。fallback option で UI 上は
    // 残るが、そのまま save すると失効 BG が RPC 送信される silent invalid
    // submission を防ぐ (物理削除は AC-06 尊重で除外、helper 参照)。
    const recordValidFrom = values.validFrom
    const isActiveForRecord = (bg: (typeof suppressionBgs)[number]): boolean =>
      (submitterAreaCode === undefined || bg.areaCode === submitterAreaCode) &&
      (recordValidFrom === undefined ||
        isActiveOn(bg.validFrom, bg.validTo, recordValidFrom))
    const suppressionBgCandidates = suppressionBgs.filter(isActiveForRecord)
    const partnerBgCandidates = partnerBgs.filter(isActiveForRecord)
    const bgOutOfPeriodMsg = t(
      'page:adminOcctoMaster.demandSuppressionPlanMaster.form.bgMasterOutOfPeriod',
    )
    // allBgMastersData 未取得時は undefined を渡し、helper 側で fail-closed
    // (候補外を全て有効期間外扱い) にする。
    const allBgMastersForValidation =
      allBgMastersData !== undefined ? allBgMasters : undefined
    const contractsSupp = findBgsOutOfPeriod(
      values.contracts.map((c) => ({ bgMasterId: c.suppressionBgMasterId })),
      suppressionBgCandidates,
      allBgMastersForValidation,
    )
    const contractsPartner = findBgsOutOfPeriod(
      values.contracts.map((c) => ({ bgMasterId: c.partnerBgMasterId })),
      partnerBgCandidates,
      allBgMastersForValidation,
    )
    const partnerBgsOut = findBgsOutOfPeriod(
      values.partnerBgs,
      partnerBgCandidates,
      allBgMastersForValidation,
    )
    if (
      contractsSupp.length > 0 ||
      contractsPartner.length > 0 ||
      partnerBgsOut.length > 0
    ) {
      for (const { index } of contractsSupp) {
        setError(`contracts.${index}.suppressionBgMasterId`, {
          message: bgOutOfPeriodMsg,
        })
      }
      for (const { index } of contractsPartner) {
        setError(`contracts.${index}.partnerBgMasterId`, {
          message: bgOutOfPeriodMsg,
        })
      }
      for (const { index } of partnerBgsOut) {
        setError(`partnerBgs.${index}.bgMasterId`, {
          message: bgOutOfPeriodMsg,
        })
      }
      return false
    }
    return true
  }

  const handleSave = (): void => {
    if (!validateOutOfPeriodBeforeSubmit()) return
    hookHandleSave()
  }

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #pm-supp-modal) は `.ecl-modal--xl`
      // (需要抑制契約 BG / 取引先BG 調達・販売 の 3 段カードリピータを持つ)。
      size="xl"
      title={t(
        'page:adminOcctoMaster.demandSuppressionPlanMaster.modal.createTitle',
      )}
    >
      <DemandSuppressionPlanMasterFormFields
        allBgMasters={allBgMasters}
        businessMasters={businessMasters}
        control={control}
        errors={errors}
        isEdit={false}
        isLoadingFormOptions={isLoadingFormOptions}
        partnerBgs={partnerBgs}
        setValue={setValue}
        submitterAreaCode={submitterAreaCode}
        submitterCandidates={submitterCandidates}
        submitters={submitters}
        suppressionBgs={suppressionBgs}
      />
    </CrudModalLayout>
  )
}
